import { AxiosError, AxiosResponse } from "axios";
import { isNil, isUndefined } from "lodash";
import { AnyAction, Dispatch } from "redux";
import * as entryRightsApi from "../api/entryRights.api";
import { DateFilter } from "../components/DataTable/DataTableContext/DataTableContextProvider";
import { FmpFilters } from "../constants/filtering.constants";
import { EntryRightDelegeesWithPagination } from "../models/delegations/DelegatedEntryRight";
import { WithFiltersAndPaging } from "../models/filters/WithFiltersAndPaging";
import { ForFleetManager } from "../models/ForFleetManager";
import { RevokeEntryRightsModel } from "../models/vehicle-assignments/RevokeEntryRightsModel";
import {
    clearDelegeeDetailsAction,
    deleteEntryDelegeesFailureAction,
    deleteEntryDelegeesRequestAction,
    deleteEntryDelegeesSuccessAction,
    disposeErrorAction,
    getEntryRightDelegeesMyFleetFailureAction,
    getEntryRightDelegeesMyFleetRequestAction,
    getEntryRightDelegeesMyFleetSuccessAction,
    revokeEntryRightsFailureAction,
    revokeEntryRightsRequestAction,
    revokeEntryRightsSuccessAction,
} from "../store/actions/entryRights.actions";
import { toastTriggers } from "../ui/CustomToast/toastTriggers";
import { convertDateToIsoString, getLocalizedDate } from "../utils/date.utils";
import { DeleteDelegeesPayload } from "../models/vehicle-assignments/DeleteDelegeesPayload";
import { ContractType, ParkingProductType } from "../models/ParkingProductModel";
import { ProductContractTypeFilter } from "../api/common";
import { RevokeEntryRightsResponse } from "../models/delegations/RevokeEntryRightsResponse";
import { checkForSeasonTicketCrmIdNotValidError } from "../utils";
import { ErrorDetailsModel } from "../models/ErrorDetailsModel";

export function clearDelegeeDetails() {
    return (dispatch: Dispatch<AnyAction>) => {
        dispatch(clearDelegeeDetailsAction);
    }
}

export function disposeError() {
    return (dispatch: Dispatch<AnyAction>) => {
        dispatch(disposeErrorAction);
    };
}

export type RevokeProductType = 'parking' | 'pedestrian';

export function revokeEntryRights(
    model: RevokeEntryRightsModel,
    revokeProductType: RevokeProductType,
    callback?: () => void
) {
    return (dispatch: Dispatch<AnyAction>) => {
        dispatch(revokeEntryRightsRequestAction());
        const translationPrefix = revokeProductType === 'parking'
            ? "parkingRights.revokeParkingRights"
            : "parkingRights.revokePedestrianRight";

        entryRightsApi
            .revokeEntryRights(model)
            .then(({ data }: { data: RevokeEntryRightsResponse }) => {
                dispatch(revokeEntryRightsSuccessAction({ model, response: data }));

                if (!isUndefined(callback)) {
                    callback();
                }

                toastTriggers.success({
                    content: {
                        key: `${translationPrefix}.success`,
                        options: { count: model.delegeeRevokeModels.flatMap(d => d.products).length },
                    },
                });
            })
            .catch((error: AxiosError) => {
                dispatch(revokeEntryRightsFailureAction());

                if (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
                    toastTriggers.error({
                        content: {
                            key: "errors.invalidSeasonTicketCrmId",
                        },
                    });
                } else {
                    toastTriggers.error({
                        content: {
                            key: `${translationPrefix}.error`,
                        },
                    });
                }
            });
    };
};

export function deleteEntryRightDelegees(
    payload: DeleteDelegeesPayload,
    callback?: () => void
) {
    return (dispatch: Dispatch<AnyAction>) => {
        dispatch(deleteEntryDelegeesRequestAction());

        entryRightsApi
            .deleteEntryRightDelegees(payload.model)
            .then(() => {
                dispatch(deleteEntryDelegeesSuccessAction(payload));

                if (!isUndefined(callback)) {
                    callback();
                }

                toastTriggers.success({
                    content: {
                        key: "parkingRights.deletePedestrianRight.success",
                        options: { count: payload.model.delegeeIds.length },
                    },
                });
            })
            .catch((error: AxiosError) => {
                dispatch(deleteEntryDelegeesFailureAction());

                if (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
                    toastTriggers.error({
                        content: {
                            key: "errors.invalidSeasonTicketCrmId",
                        },
                    });
                } else {
                    toastTriggers.error({
                        content: {
                            key: "parkingRights.deletePedestrianRight.error",
                        },
                    });
                }
            });
    };
};

export function getEntryRightDelegeesMyFleet(request: WithFiltersAndPaging<ForFleetManager>, productContractTypeFilter: ProductContractTypeFilter) {
    return (dispatch: Dispatch<AnyAction>) => {
        dispatch(getEntryRightDelegeesMyFleetRequestAction(request));

        entryRightsApi.getDelegeesMyFleet(request, productContractTypeFilter)
            .then((response: AxiosResponse<EntryRightDelegeesWithPagination>) => {
                dispatch(getEntryRightDelegeesMyFleetSuccessAction(response.data));
            })
            .catch((error: AxiosError) => { dispatch(getEntryRightDelegeesMyFleetFailureAction(error)); })
    };
}

export type ContractProductTypes = {
    contractType?: ContractType;
    productType?: ParkingProductType;
}

export function convertFiltersToGetDelegatedEntryRightsQuery(
    r: WithFiltersAndPaging<ForFleetManager>,
    { contractType, productType }: ContractProductTypes
): entryRightsApi.GetDelegatedEntryRightsQuery {
    const { filters } = r;
    const invitationDateFilter = filters?.find((x) => x.key === FmpFilters.invitationDate)?.value as
        | DateFilter
        | undefined;

    const convertedInvitationDateFilter =
        isNil(invitationDateFilter) || isNil(invitationDateFilter.startDate) || isNil(invitationDateFilter.endDate)
            ? undefined
            : convertDateToIsoString(invitationDateFilter.startDate, invitationDateFilter.endDate);
    const convertDateFilterLocalized = (key: string) => {
        const dateFilter = filters?.find((x) => x.key === key)?.value as DateFilter | undefined;
        return {
            start: getLocalizedDate(dateFilter?.startDate) || undefined,
            end: getLocalizedDate(dateFilter?.endDate, true) || undefined
        };
    };

    const convertedActivationDateFilter = convertDateFilterLocalized(FmpFilters.activationDate);
    const convertedExpirationDateFilter = convertDateFilterLocalized(FmpFilters.expirationDate);

    return {
        seasonTicketOwnerCrmId: r.entity.seasonTicketOwnerCrmId,
        placeId: Number(filters?.find((x) => x.key === FmpFilters.placeId)?.value),
        language: r.entity.language,
        page: r.pagination.pageNumber,
        pageSize: r.pagination.pageSize,
        invitationFrom: convertedInvitationDateFilter?.start,
        invitationTo: convertedInvitationDateFilter?.end,
        activationFrom: convertedActivationDateFilter?.start,
        activationTo: convertedActivationDateFilter?.end,
        expirationFrom: convertedExpirationDateFilter?.start,
        expirationTo: convertedExpirationDateFilter?.end,
        statuses: filters?.find((x) => x.key === FmpFilters.statuses)?.value as any,
        ascending: r.filters?.find((x) => x.key === FmpFilters.orderDesc)?.value === "false",
        email: filters?.find((x) => x.key === FmpFilters.email)?.value as any,
        name: filters?.find((x) => x.key === FmpFilters.name)?.value as any,
        pmcIds: filters?.find((x) => x.key === FmpFilters.products)?.value as any,
        sortBy: filters?.find((x) => x.key === FmpFilters.sortBy)?.value as any,
        contractType,
        productType,
    };
}